// Solomon Portal v2.x \u2014 ConversationThread.
//
// Renders the customer-visible thread bubbles for a single Help Scout
// conversation. The Edge Function (`get-conversation`, extended for issue #9)
// is responsible for filtering threads to only the ones a customer should
// see (i.e. dropping internal-only notes); this component trusts the
// upstream filter and renders what it gets.
//
// Bubble styles:
//   - `customer` \u2014 messages authored by the portal user (right-aligned, peach surface).
//   - `staff`    \u2014 messages from Solomon staff (left-aligned, white surface).
//   - `system`   \u2014 status changes / forwards / phone (centered, muted).
//
// Body rendering (ADR-035):
//   Help Scout stores thread bodies as HTML — inbound emails arrive as
//   multipart/alternative and HS picks the HTML part, so the `body` we
//   get on the wire is real HTML markup (`<div dir="ltr">`, `<br>`,
//   `<a href="...">`, etc.). We render it via `dangerouslySetInnerHTML`
//   AFTER passing it through DOMPurify with a strict allowlist (see
//   `src/lib/sanitize-html.ts` — the security boundary). When DOMPurify
//   returns an empty string for a non-empty input (e.g. a body that is
//   100% disallowed tags), we fall back to a naive HTML→plain-text
//   render so the user sees something rather than a blank bubble.

import * as React from 'react';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { sanitizeThreadHtml, htmlToPlainText } from '../../lib/sanitize-html';

export interface ConversationThreadEntry {
  id: number;
  type: string;
  /** ISO timestamp. */
  createdAt: string;
  /**
   * Thread body as HTML (per Help Scout's mailbox API — thread bodies
   * are stored as the HTML part of the original email). Sanitized at
   * render time by `sanitizeThreadHtml`. ADR-035.
   */
  body: string;
  /** Author info as forwarded from HS. The `type` is the HS author kind —
   *  in practice `customer` or `user`, but HS reserves the right to introduce
   *  new values, so the type is left as a plain string. */
  createdBy?: {
    type: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  /** Some thread types (`status`, `phone`, `customer`) may have no body. */
  status?: string;
}

interface ConversationThreadProps {
  threads: ConversationThreadEntry[];
}

type BubbleSide = 'customer' | 'staff' | 'system';

function classifyThread(t: ConversationThreadEntry): BubbleSide {
  // System-style threads: status changes, phone-only, forwarded, etc.
  const sysTypes = new Set(['lineitem', 'status', 'forwardparent', 'forwardchild', 'phone']);
  if (sysTypes.has(t.type)) return 'system';
  if (t.createdBy?.type === 'customer') return 'customer';
  return 'staff';
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function authorLabel(t: ConversationThreadEntry, side: BubbleSide): string {
  if (side === 'system') return 'System';
  const cb = t.createdBy;
  if (!cb) return side === 'customer' ? 'You' : 'Solomon Advising';
  const first = cb.firstName ?? '';
  const last = cb.lastName ?? '';
  const name = `${first} ${last}`.trim();
  if (name.length > 0) return name;
  if (cb.email) return cb.email;
  return side === 'customer' ? 'You' : 'Solomon Advising';
}

/**
 * Render a single thread's body as sanitized HTML, falling back to a
 * plain-text render if the sanitizer drops everything (e.g. a body of
 * pure disallowed tags). ADR-035.
 */
function ThreadBody({ body }: { body: string }): React.ReactNode {
  const safe = React.useMemo(() => sanitizeThreadHtml(body), [body]);
  // If sanitization produced nothing usable (empty string from a
  // non-empty input), fall back to the naive HTML→plain-text path so
  // we never render a blank bubble. This is the SSR-safe path too.
  if (safe.trim().length === 0 && body.trim().length > 0) {
    return (
      <p className="whitespace-pre-wrap text-[14px] leading-relaxed">{htmlToPlainText(body)}</p>
    );
  }
  return (
    <div
      className="thread-body text-[14px] leading-relaxed"
      // sanitizeThreadHtml is the security boundary; `safe` is a branded
      // SafeHtml string produced by DOMPurify with a strict allowlist.
      // ADR-035.
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

export function ConversationThread({ threads }: ConversationThreadProps): React.ReactNode {
  if (threads.length === 0) {
    return (
      <p role="status" className="text-text-muted">
        No replies yet.
      </p>
    );
  }
  // HS returns threads newest-first; reverse to render chronologically (oldest at top).
  const ordered = [...threads].reverse();
  return (
    <ol className="flex flex-col gap-4" aria-label="Conversation messages">
      {ordered.map((t) => {
        const side = classifyThread(t);
        if (side === 'system') {
          // Pick the first non-empty system label — nullish-coalescing fits
          // because empty string is *meaningfully* missing here, but eslint
          // prefers `??` over `||`. Build the label explicitly.
          const sysLabel =
            t.body.length > 0 ? t.body : (t.status ?? '').length > 0 ? t.status : 'Status update';
          return (
            <li key={t.id} className="flex justify-center">
              <p className="text-[12px] uppercase tracking-[0.08em] text-text-muted">
                {sysLabel} • {formatTimestamp(t.createdAt)}
              </p>
            </li>
          );
        }
        const isCustomer = side === 'customer';
        return (
          <li
            key={t.id}
            className={cn('flex w-full', isCustomer ? 'justify-end' : 'justify-start')}
          >
            <Card
              className={cn(
                // Bubble surfaces (ADR-035 v3): paragraph-grade reading
                // surfaces with proper contrast. Customer-side gets a
                // soft peach tint; staff-side is plain white with a
                // thin border. Both use --color-text-dark for body
                // text so paragraphs are readable at body sizes.
                'max-w-[85%] border',
                isCustomer
                  ? 'bg-thread-customer-bg text-thread-customer-fg border-thread-customer-border'
                  : 'bg-thread-staff-bg text-thread-staff-fg border-thread-staff-border',
              )}
            >
              <CardContent className="p-4">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-medium text-text-dark">
                    {authorLabel(t, side)}
                  </span>
                  <time dateTime={t.createdAt} className="text-[12px] text-text-muted">
                    {formatTimestamp(t.createdAt)}
                  </time>
                </div>
                <ThreadBody body={t.body} />
              </CardContent>
            </Card>
          </li>
        );
      })}
    </ol>
  );
}
